<template>
  <Dialog
    class="quick-search-dialog"
    :visible="show"
    :style="{ width: $isMobile() ? '96vw' : '760px' }"
    :header="dialogTitle"
    :modal="true"
    :dismissableMask="true"
    :focusOnShow="false"
    :closeOnEscape="true"
    @update:visible="onDialogVisibleUpdate"
    @show="focusSearchInput"
    @hide="closeDialog"
  >
    <div class="quick-search-body">
      <div class="quick-search-hero">
        <div class="quick-search-hero__content">
          <span class="quick-search-hero__eyebrow">Acesso r&aacute;pido</span>
          <div class="quick-search-hero__title">
            <span class="quick-search-hero__icon">
              <i :class="activeScope.icon"></i>
            </span>
            <div>
              <div class="quick-search-hero__heading">{{ activeScope.label }}</div>
              <p class="quick-search-hero__description">
                Encontre rapidamente o registo certo e abra-o sem sair do helpdesk.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="scope-selector" role="group" aria-label="Âmbito de pesquisa">
        <button
          v-for="(scope, index) in scopes"
          :key="scope.key"
          :ref="`scopeButton-${index}`"
          type="button"
          class="scope-chip"
          :class="{ active: scope.key === selectedScope }"
          :aria-label="`Pesquisar ${scope.label}`"
          :aria-pressed="scope.key === selectedScope ? 'true' : 'false'"
          @click="onScopeChange(scope.key)"
          @keydown="onScopeButtonKeydown(index, $event)"
        >
          {{ scope.label }}
        </button>
      </div>

      <div class="search-panel">
        <div class="search-panel__header">
          <label class="search-panel__label" for="helpdesk-quick-search-input">
            {{ searchPlaceholder }}
          </label>
          <span class="search-panel__hint">M&iacute;nimo de 1 car&aacute;cter</span>
        </div>

        <div class="p-inputgroup quick-search-inputgroup">
          <span class="p-inputgroup-addon quick-search-leading-addon">
            <i class="pi pi-search"></i>
          </span>
          <InputText
            id="helpdesk-quick-search-input"
            ref="searchInput"
            v-model="term"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            autocomplete="off"
            @input="onTermInput"
            @keydown="onInputKeydown"
          />
          <Button
            icon="pi pi-search"
            class="p-button-warning quick-search-action-button"
            @click="onManualSearch"
          />
        </div>
      </div>

      <div v-if="loading || errorMessage || (results.length === 0 && hasSearched)" class="state-card">
        <div v-if="loading" class="state-row">
          <span class="state-row__icon state-row__icon--loading">
            <i class="pi pi-spin pi-spinner"></i>
          </span>
          <div>
            <div class="state-row__title">{{ loadingText }}</div>
            <div class="state-row__description">Aguarde um instante enquanto reunimos os resultados.</div>
          </div>
        </div>

        <div v-else-if="errorMessage" class="state-row state-row--error">
          <span class="state-row__icon state-row__icon--error">
            <i class="pi pi-exclamation-circle"></i>
          </span>
          <div>
            <div class="state-row__title">{{ errorMessage }}</div>
            <div class="state-row__description">Tente novamente ou ajuste o termo pesquisado.</div>
          </div>
        </div>

        <div v-else class="state-row">
          <span class="state-row__icon">
            <i class="pi pi-inbox"></i>
          </span>
          <div>
            <div class="state-row__title">{{ emptyText }}</div>
            <div class="state-row__description">Experimente outro termo ou mude o &acirc;mbito da pesquisa.</div>
          </div>
        </div>
      </div>

      <div v-if="hasSearched && !loading && !errorMessage" class="results-summary">
        <div class="results-summary__label">
          <span class="results-summary__scope">
            <i :class="activeScope.icon"></i>
            {{ activeScope.label }}
          </span>
          <span class="results-summary__text">{{ resultsSummary }}</span>
        </div>
      </div>

      <div v-if="results.length > 0" class="results-list">
        <a
          v-for="(result, index) in results"
          :key="`${selectedScope}-${result.id}-${index}`"
          :href="resolveRoute(result.route)"
          target="_blank"
          rel="noopener noreferrer"
          :class="['result-item', { highlighted: index === highlightedIndex }]"
          @mouseenter="highlightedIndex = index"
          @click="openInNewTab($event, result)"
          @auxclick="openInNewTab($event, result)"
        >
          <span class="result-icon">
            <i :class="activeScope.icon"></i>
          </span>

          <span class="result-main">
            <span class="result-title">{{ result.title }}</span>
            <span class="result-meta">
              <span v-if="result.subtitle">{{ result.subtitle }}</span>
              <Tag
                v-if="result.statusTag"
                :value="result.statusTag.label"
                :severity="result.statusTag.severity"
                class="result-status-tag"
              />
            </span>
          </span>

          <span class="result-action">
            <i class="pi pi-chevron-right"></i>
          </span>
        </a>
      </div>
    </div>
  </Dialog>
</template>

<script>
import quickSearchService from "@/services/quick-search.service";

export default {
  name: "HelpdeskQuickSearch",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      term: "",
      selectedScope: "tickets",
      scopes: [
        {
          key: "tickets",
          label: "Tickets",
          icon: "pi pi-comments",
          placeholder: "Pesquisar tickets",
          loadingText: "A pesquisar tickets...",
          emptyText: "Nenhum ticket encontrado.",
          errorText: "Erro ao pesquisar tickets.",
        },
        {
          key: "contracts",
          label: "Contratos",
          icon: "pi pi-file",
          placeholder: "Pesquisar contratos",
          loadingText: "A pesquisar contratos...",
          emptyText: "Nenhum contrato encontrado.",
          errorText: "Erro ao pesquisar contratos.",
        },
        {
          key: "installations",
          label: "Instalações",
          icon: "pi pi-home",
          placeholder: "Pesquisar instalações",
          loadingText: "A pesquisar instalações...",
          emptyText: "Nenhuma instalação encontrada.",
          errorText: "Erro ao pesquisar instalações.",
        },
        {
          key: "pats",
          label: "Pats",
          icon: "pi pi-tag",
          placeholder: "Pesquisar pats",
          loadingText: "A pesquisar pats...",
          emptyText: "Nenhum pat encontrado.",
          errorText: "Erro ao pesquisar pats.",
        },
      ],
      loading: false,
      hasSearched: false,
      results: [],
      highlightedIndex: -1,
      errorMessage: null,
      searchTimer: null,
      searchRequestId: 0,
      focusRetryTimers: [],
      previousFocusedElement: null,
      isClosing: false,
    };
  },
  computed: {
    activeScope() {
      return this.scopes.find((scope) => scope.key === this.selectedScope) || this.scopes[0];
    },
    dialogTitle() {
      return `Pesquisa Rapida - ${this.activeScope.label}`;
    },
    searchPlaceholder() {
      return this.activeScope.placeholder;
    },
    loadingText() {
      return this.activeScope.loadingText;
    },
    emptyText() {
      return this.activeScope.emptyText;
    },
    resultsSummary() {
      if (this.results.length === 0) return "Sem resultados";
      if (this.results.length === 1) return "1 resultado encontrado";
      return `${this.results.length} resultados encontrados`;
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.capturePreviousFocusedElement();
        this.isClosing = false;
        this.resetForOpen();
      } else {
        this.clearSearchTimer();
        this.clearFocusRetryTimers();
        if (!this.isClosing) {
          this.restorePreviousFocus();
        }
      }
    },
  },
  methods: {
    capturePreviousFocusedElement() {
      const active = document.activeElement;
      this.previousFocusedElement = active && typeof active.focus === "function" ? active : null;
    },
    restorePreviousFocus() {
      const element = this.previousFocusedElement;
      this.previousFocusedElement = null;
      if (!element || typeof element.focus !== "function" || !document.contains(element)) return;
      setTimeout(() => {
        element.focus({ preventScroll: true });
      }, 0);
    },
    getScopeByKey(scopeKey) {
      return this.scopes.find((scope) => scope.key === scopeKey) || this.scopes[0];
    },
    getScopeIndex(scopeKey) {
      return this.scopes.findIndex((scope) => scope.key === scopeKey);
    },
    normalizeText(value) {
      return String(value || "").trim();
    },
    compactSubtitle(values) {
      return values
        .map((value) => this.normalizeText(value))
        .filter((value) => value.length > 0)
        .join(" • ");
    },
    resetResultsState() {
      this.results = [];
      this.highlightedIndex = -1;
    },
    invalidatePendingSearch() {
      this.searchRequestId += 1;
      this.loading = false;
    },
    resetForOpen() {
      this.term = "";
      this.selectedScope = "tickets";
      this.loading = false;
      this.hasSearched = false;
      this.resetResultsState();
      this.errorMessage = null;
      this.clearSearchTimer();
      this.focusSearchInput();
    },
    focusSearchInputOnce() {
      const searchRef = this.$refs.searchInput;
      let inputEl = null;

      if (searchRef && searchRef.$el) {
        const foundInput = searchRef.$el.querySelector("input");
        inputEl = foundInput || searchRef.$el;
      } else {
        inputEl = searchRef;
      }

      if (!inputEl || typeof inputEl.focus !== "function") return false;

      inputEl.focus({ preventScroll: true });
      if (typeof inputEl.setSelectionRange === "function") {
        const length = (inputEl.value || "").length;
        inputEl.setSelectionRange(length, length);
      }

      return document.activeElement === inputEl;
    },
    focusSearchInput() {
      this.clearFocusRetryTimers();
      const focusDelays = [0, 40, 100, 180, 280, 420];

      this.$nextTick(() => {
        focusDelays.forEach((delay) => {
          const timer = setTimeout(() => {
            if (!this.show) return;
            this.focusSearchInputOnce();
          }, delay);
          this.focusRetryTimers.push(timer);
        });
      });
    },
    clearSearchTimer() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
    },
    clearFocusRetryTimers() {
      if (!Array.isArray(this.focusRetryTimers)) return;
      this.focusRetryTimers.forEach((timer) => clearTimeout(timer));
      this.focusRetryTimers = [];
    },
    closeDialog() {
      if (this.isClosing) return;
      this.isClosing = true;
      this.clearSearchTimer();
      this.clearFocusRetryTimers();
      this.$emit("close");
      this.restorePreviousFocus();
    },
    onDialogVisibleUpdate(value) {
      if (value) return;
      this.closeDialog();
    },
    onScopeButtonKeydown(index, event) {
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (index + direction + this.scopes.length) % this.scopes.length;
      const nextScope = this.scopes[nextIndex];
      this.onScopeChange(nextScope.key);
      this.$nextTick(() => {
        const refTarget = this.$refs[`scopeButton-${nextIndex}`];
        const button = Array.isArray(refTarget) ? refTarget[0] : refTarget;
        if (button && typeof button.focus === "function") {
          button.focus({ preventScroll: true });
        }
      });
    },
    onScopeChange(scopeKey) {
      if (scopeKey === this.selectedScope) return;
      this.selectedScope = scopeKey;
      this.errorMessage = null;
      this.clearSearchTimer();
      this.resetResultsState();

      const trimmed = this.normalizeText(this.term);
      if (trimmed.length < 1) {
        this.hasSearched = false;
        this.invalidatePendingSearch();
        return;
      }

      this.executeSearch(trimmed);
    },
    onTermInput() {
      this.highlightedIndex = -1;
      this.errorMessage = null;
      this.clearSearchTimer();

      const trimmed = this.normalizeText(this.term);
      if (trimmed.length < 1) {
        this.loading = false;
        this.hasSearched = false;
        this.invalidatePendingSearch();
        this.resetResultsState();
        return;
      }

      this.searchTimer = setTimeout(() => {
        this.executeSearch(trimmed);
      }, 300);
    },
    onManualSearch() {
      const trimmed = this.normalizeText(this.term);
      this.clearSearchTimer();
      if (trimmed.length < 1) {
        this.errorMessage = "Introduza pelo menos 1 carácter.";
        this.resetResultsState();
        this.loading = false;
        return;
      }
      this.executeSearch(trimmed);
    },
    async dispatchSearchByScope(scopeKey, term) {
      switch (scopeKey) {
        case "tickets":
          return quickSearchService.searchTickets(term);
        case "contracts":
          return quickSearchService.searchContracts(term);
        case "installations":
          return quickSearchService.searchInstallations(term);
        case "pats":
          return quickSearchService.searchPats(term);
        default:
          return null;
      }
    },
    normalizeResultItem(raw, scopeKey) {
      if (!raw || typeof raw !== "object") return null;

      if (scopeKey === "tickets") {
        const id = raw.id;
        if (!id) return null;
        return {
          id,
          title: `#${id} - ${this.normalizeText(raw.subject) || "Sem assunto"}`,
          subtitle: this.compactSubtitle([
            raw.client_email,
            raw.ticket_status && raw.ticket_status.name,
          ]),
          route: `/helpdesk-tickets-detail2/${id}`,
        };
      }

      if (scopeKey === "contracts") {
        const id = raw.id;
        const contractId = raw.contract_id;
        if (!id || !contractId) return null;
        const clientName = this.normalizeText(raw.client_name);
        const contractLabel =
          this.normalizeText(raw.designation) ||
          this.normalizeText(raw.description) ||
          this.normalizeText(raw.product);

        return {
          id,
          title: clientName && contractLabel ? `${clientName} - ${contractLabel}` : clientName || contractLabel || `Contrato #${id}`,
          subtitle: this.compactSubtitle([
            raw.entity_name,
            raw.localname || raw.entity_location,
            raw.product,
            raw.status === "active" && "Ativo",
            raw.end_date && `Fim: ${raw.end_date}`,
          ]),
          statusTag: {
            expired: { label: "Expirado", severity: "danger" },
            inactive: { label: "Inativo", severity: "warning" },
          }[raw.status],
          route: `/contracts/details/${contractId}`,
        };
      }

      if (scopeKey === "installations") {
        const id = raw.id;
        if (!id) return null;
        return {
          id,
          title: `Instalação #${id} - ${this.normalizeText(raw.name) || "Sem nome"}`,
          subtitle: this.compactSubtitle([raw.description, raw.seller, raw.manager]),
          route: `/installation-details/${id}`,
        };
      }

      if (scopeKey === "pats") {
        const id = raw.id;
        if (!id) return null;
        return {
          id,
          title: `Pat #${id} - ${this.normalizeText(raw.name) || "Sem nome"}`,
          subtitle: this.compactSubtitle([raw.resume, raw.problem]),
          route: `/pat-details2/${id}`,
        };
      }

      return null;
    },
    normalizeSearchResults(scopeKey, response) {
      const source =
        scopeKey === "tickets" && response && Array.isArray(response.data)
          ? response.data
          : response;
      if (!Array.isArray(source)) return [];
      return source
        .map((item) => this.normalizeResultItem(item, scopeKey))
        .filter((item) => item && item.id && item.route)
        .slice(0, 10);
    },
    async executeSearch(term) {
      const requestId = ++this.searchRequestId;
      const scopeKey = this.selectedScope;
      const scope = this.getScopeByKey(scopeKey);
      this.loading = true;
      this.hasSearched = true;
      this.errorMessage = null;

      const response = await this.dispatchSearchByScope(scopeKey, term);

      if (requestId !== this.searchRequestId) return;
      this.loading = false;

      if (!response) {
        this.errorMessage = scope.errorText;
        this.resetResultsState();
        return;
      }

      this.results = this.normalizeSearchResults(scopeKey, response);
      this.highlightedIndex = this.results.length > 0 ? 0 : -1;
    },
    onInputKeydown(event) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        this.moveHighlight(-1);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        this.openFromKeyboard();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeDialog();
      }
    },
    moveHighlight(step) {
      if (!this.results.length) return;
      if (this.highlightedIndex < 0) {
        this.highlightedIndex = 0;
        return;
      }
      const nextIndex = this.highlightedIndex + step;
      if (nextIndex < 0) {
        this.highlightedIndex = 0;
        return;
      }
      if (nextIndex >= this.results.length) {
        this.highlightedIndex = this.results.length - 1;
        return;
      }
      this.highlightedIndex = nextIndex;
    },
    resolveRoute(route) {
      if (!this.$router) return "#";
      const resolved = this.$router.resolve({ path: route });
      return resolved ? resolved.href : "#";
    },
    openInNewTab(event, result) {
      if (event && event.type === "auxclick" && event.button !== 1) return;
      if (!result || !result.route) return;
    },
    openFromKeyboard() {
      if (
        this.results.length > 0 &&
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.results.length
      ) {
        const resultItem = this.$el.querySelectorAll(".result-item")[this.highlightedIndex];
        if (resultItem) {
          resultItem.click();
        }
        return;
      }
      this.onManualSearch();
    },
  },
  beforeDestroy() {
    this.clearSearchTimer();
    this.clearFocusRetryTimers();
  },
};
</script>

<style scoped lang="scss">
.quick-search-dialog :deep(.p-dialog) {
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.2);
}

.quick-search-dialog :deep(.p-dialog-header) {
  padding: 1.2rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 62%);
}

.quick-search-dialog :deep(.p-dialog-title) {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1f2937;
}

.quick-search-dialog :deep(.p-dialog-content) {
  padding: 1.35rem 1.5rem 1.5rem;
  background: linear-gradient(180deg, #f9fbff 0%, #ffffff 38%);
}

.quick-search-dialog :deep(.p-dialog-header-icon) {
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  color: #6b7280;
  transition: background 0.2s ease, color 0.2s ease;
}

.quick-search-dialog :deep(.p-dialog-header-icon:hover) {
  background: rgba(3, 136, 229, 0.12);
  color: #007ad9;
}

.quick-search-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quick-search-hero {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border: 1px solid #d7e8fb;
  border-radius: 18px;
  background: linear-gradient(135deg, #f6faff 0%, #ffffff 100%);
}

.quick-search-hero__content {
  min-width: 0;
  width: 100%;
}

.quick-search-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: rgba(3, 136, 229, 0.1);
  color: #007ad9;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.quick-search-hero__title {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-top: 0.85rem;
}

.quick-search-hero__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #007ad9 0%, #1fa1fc 100%);
  color: #ffffff;
  font-size: 1.1rem;
  box-shadow: 0 10px 24px rgba(0, 122, 217, 0.22);
}

.quick-search-hero__heading {
  color: #111827;
  font-size: 1.15rem;
  font-weight: 700;
}

.quick-search-hero__description {
  margin: 0.35rem 0 0;
  color: #6b7280;
  font-size: 0.93rem;
  line-height: 1.45;
}

.scope-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  padding: 0.35rem;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
}

.scope-chip {
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: #374151;
  padding: 0.55rem 1rem;
  min-height: 2.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: all 0.18s ease;
}

.scope-chip.active {
  border-color: #007ad9;
  background: linear-gradient(135deg, #f3f9ff 0%, #eef7ff 100%);
  color: #005fa3;
  box-shadow: 0 10px 20px rgba(0, 122, 217, 0.14);
}

.scope-chip:hover {
  background: #f8fafc;
}

.search-panel {
  padding: 0.95rem 1rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.05);
}

.search-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
}

.search-panel__label {
  color: #374151;
  font-size: 0.9rem;
  font-weight: 700;
}

.search-panel__hint {
  color: #9ca3af;
  font-size: 0.8rem;
  font-weight: 600;
}

.quick-search-inputgroup {
  align-items: stretch;
}

.quick-search-leading-addon {
  border: 1px solid #dbe3ed;
  border-right: 0;
  background: #f8fafc;
  color: #007ad9;
}

.quick-search-inputgroup :deep(.p-inputtext) {
  height: 3.1rem;
  border-color: #dbe3ed;
  box-shadow: none;
}

.quick-search-inputgroup :deep(.p-inputtext:enabled:focus) {
  border-color: #007ad9;
  box-shadow: 0 0 0 3px rgba(31, 161, 252, 0.14);
}

.scope-chip:focus-visible {
  outline: 2px solid #007ad9;
  outline-offset: 2px;
}

.quick-search-action-button {
  min-width: 3.15rem;
  height: 3.1rem;
  width: 3.15rem;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background: linear-gradient(135deg, #007ad9 0%, #1fa1fc 100%);
  border-color: #007ad9;
}

.quick-search-action-button:enabled:hover {
  background: linear-gradient(135deg, #006cc0 0%, #1193f0 100%);
  border-color: #006cc0;
}

.quick-search-action-button:enabled:focus {
  box-shadow: 0 0 0 3px rgba(31, 161, 252, 0.18);
}

.quick-search-action-button :deep(.pi) {
  font-size: 1.1rem;
}

.state-card {
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.05);
}

.state-row {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  min-height: 88px;
}

.state-row__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
  font-size: 1.1rem;
}

.state-row__icon--loading {
  background: rgba(3, 136, 229, 0.12);
  color: #007ad9;
}

.state-row__icon--error {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.state-row__title {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 700;
}

.state-row__description {
  margin-top: 0.2rem;
  color: #6b7280;
  font-size: 0.84rem;
  line-height: 1.45;
}

.state-row--error .state-row__title {
  color: #b91c1c;
}

.results-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.results-summary__label {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.results-summary__scope {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #eff7ff;
  color: #007ad9;
  font-size: 0.82rem;
  font-weight: 700;
}

.results-summary__text {
  color: #6b7280;
  font-size: 0.84rem;
  font-weight: 600;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  max-height: 50vh;
  overflow: auto;
  padding-right: 0.2rem;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.18s ease;
}

.result-item:hover {
  transform: translateY(-1px);
  border-color: #b9daf9;
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.08);
}

.result-item.highlighted {
  border-color: #007ad9;
  background: linear-gradient(135deg, #f8fbff 0%, #eef7ff 100%);
  box-shadow: 0 16px 28px rgba(0, 122, 217, 0.12);
}

.result-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 14px;
  background: #eff7ff;
  color: #007ad9;
  font-size: 1rem;
}

.result-main {
  min-width: 0;
  flex: 1 1 auto;
}

.result-title {
  font-weight: 600;
  color: #1f2937;
  line-height: 1.35;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.22rem;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.4;
}

.result-status-tag {
  flex-shrink: 0;
  font-size: 0.75rem;
}

.result-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 0.85rem;
}

@media (max-width: 768px) {
  .quick-search-dialog :deep(.p-dialog-content) {
    padding: 1rem;
  }

  .quick-search-hero {
    flex-direction: column;
  }

  .search-panel__header,
  .results-summary {
    flex-direction: column;
    align-items: flex-start;
  }

  .result-item {
    align-items: flex-start;
  }

  .result-action {
    padding-top: 0.25rem;
  }
}
</style>
